/**
 * Google Ads Conversion Tracking Service
 *
 * Singleton service for Google Ads conversion tracking via GA4 Measurement Protocol.
 * All conversions are sent server-side through /api/track/conversion, which then
 * sends to GA4. Google Ads receives conversions via GA4 import.
 *
 * This approach:
 * - Bypasses ad blockers (server-side tracking)
 * - Provides complete GA4 funnel data
 * - Uses environment prefixing (dev_ for non-production)
 */

import { notNullish } from '@getstep/sdk/dist/util/Assert'
import { resolveUtmValue } from '@lib/analytics/constants'
import { generateEventId } from '@lib/analytics/utils'
import { hasCookieConsent } from '@lib/hooks/useCookieConsent'
import { localStorageWithTTL } from '@lib/store/persistence/storage'
import * as Sentry from '@sentry/nextjs'
import { isPrd } from '@shared/util/env'

import {
    CONVERSION_LABELS,
    type ConversionParams,
    getGA4EventName,
    type GoogleAdsConversionEvent,
} from './conversions'
import { type EnhancedUserData, prepareUserData } from './enhancedConversions'

function debugLog(...args: unknown[]) {
    if (!isPrd()) {
        console.info('[GoogleAdsService]', ...args)
    }
}

/**
 * Pending conversion to be sent when consent is granted
 */
interface PendingConversion {
    event: GoogleAdsConversionEvent
    params?: ConversionParams
    timestamp: number
    eventId: string
}

/**
 * Hashed user data for enhanced conversions
 */
interface HashedUserData {
    sha256_email_address?: string[]
    sha256_phone_number?: string[]
    address?: {
        sha256_first_name?: string
        sha256_last_name?: string
    }
}

class GoogleAdsService {
    private initialized = false
    private pendingConversions: PendingConversion[] = []
    private pendingUserData: EnhancedUserData | null = null
    private hashedUserData: HashedUserData | null = null
    private clientId: string | null = null
    private gclid: string | null = null
    private _flowType: string | null = null
    private _userId: string | null = null
    // UTM params for campaign attribution (current/last-touch)
    private utmSource: string | null = null
    private utmMedium: string | null = null
    private utmCampaign: string | null = null
    private utmTerm: string | null = null
    private utmContent: string | null = null
    // Google Ads specific params
    private adset: string | null = null
    private network: string | null = null
    private matchtype: string | null = null
    private device: string | null = null
    // Original params for first-touch attribution (persisted in localStorage)
    private originalUtmSource: string | null = null
    private originalUtmMedium: string | null = null
    private originalUtmCampaign: string | null = null
    private originalUtmTerm: string | null = null
    private originalUtmContent: string | null = null
    private originalAdset: string | null = null
    private originalNetwork: string | null = null
    private originalMatchtype: string | null = null
    private originalDevice: string | null = null
    private _isRecoveryFlow: boolean = false
    private readonly MAX_PENDING_CONVERSIONS = 100
    private readonly STORAGE_KEY = 'step_google_ads_pending'
    private readonly CLIENT_ID_KEY = 'step_ga4_client_id'
    private readonly GCLID_KEY = 'step_google_ads_gclid'
    private readonly UTM_PARAMS_KEY = 'step_google_ads_utm_params'
    private readonly ORIGINAL_UTM_PARAMS_KEY = 'step_google_ads_original_utm'
    // TTL for original UTM params in localStorage (30 days in minutes)
    private readonly ORIGINAL_UTM_TTL_MINUTES = 30 * 24 * 60
    // GA4 Measurement ID for reading session cookie (strip 'G-' prefix for cookie name)
    private readonly GA4_MEASUREMENT_ID =
        process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID || 'G-QCP7BKQ7WJ'

    /**
     * Get the current flow type
     */
    get flowType(): string | null {
        return this._flowType
    }

    /**
     * Get the current user ID
     */
    get userId(): string | null {
        return this._userId
    }

    /**
     * Get whether this is a recovery flow
     */
    get isRecoveryFlow(): boolean {
        return this._isRecoveryFlow
    }

    /**
     * Check if user has given consent for analytics tracking
     */
    private get hasCookieConsent(): boolean {
        return hasCookieConsent()
    }

    /**
     * Set the enrollment flow type for conversion tracking
     */
    set flowType(value: string | null) {
        if (value === this._flowType) return
        this._flowType = value
        debugLog('set flowType:', value)
    }

    /**
     * Set whether this is a recovery flow for conversion tracking segmentation.
     * Recovery users are returning users re-verifying their account.
     */
    set isRecoveryFlow(value: boolean) {
        if (value === this._isRecoveryFlow) return
        this._isRecoveryFlow = value
        debugLog('set isRecoveryFlow:', value)
    }

    /**
     * Set the user ID for cross-device session stitching in GA4.
     * This helps GA4 link sessions across devices for the same user.
     * Call this after user identification (e.g., after login or KYC success).
     */
    set userId(value: string | null) {
        if (!value) return

        this._userId = value
        debugLog('User ID set:', value)
    }

    /**
     * Initialize the Google Ads tracking service
     */
    initialize(): void {
        if (this.initialized || typeof window === 'undefined') {
            return
        }

        const conversionId = process.env.NEXT_PUBLIC_GOOGLE_ADS_ID
        const enabled = process.env.NEXT_PUBLIC_GOOGLE_ADS_ENABLED === 'true'

        if (!enabled || !conversionId) {
            debugLog('Disabled or missing conversion ID')
            return
        }

        this.loadPendingFromStorage()
        this.initializeClientId()
        this.loadGclidFromStorage()
        this.initializeUtmParamsFromUrl()

        this.initialized = true

        // Process any pending conversions if consent already granted
        if (this.hasCookieConsent) {
            this.processPendingConversions()
        }
    }

    /**
     * Check if service is initialized and enabled
     */
    isInitialized(): boolean {
        return this.initialized
    }

    /**
     * Get the client ID for GA4 tracking.
     * Re-checks for GA4's _ga cookie each time to handle the case where
     * GA4 sets the cookie after our initial initialization.
     */
    getClientId(): string {
        // Always try to get the latest GA4 client ID from cookie
        // This handles the race condition where GA4 sets the cookie after we initialize
        const gaClientId = this.getGA4ClientIdFromCookie()
        if (gaClientId && gaClientId !== this.clientId) {
            this.clientId = gaClientId
            // Only persist to localStorage if consent granted (GDPR compliance)
            if (this.hasCookieConsent) {
                try {
                    localStorage.setItem(this.CLIENT_ID_KEY, gaClientId)
                } catch {
                    // Ignore storage errors
                }
            }
            debugLog('Updated to GA4 _ga cookie client ID:', gaClientId)
        }

        return this.clientId || crypto.randomUUID()
    }

    /**
     * Set the Google Click ID (GCLID) for conversion attribution.
     * The GCLID is captured from URL parameters when users arrive from Google Ads.
     * It is persisted in sessionStorage and included in all conversion payloads.
     */
    setGclid(gclid: string): void {
        if (!gclid) return

        this.gclid = gclid
        this.saveGclidToStorage()
        debugLog('GCLID set:', gclid)
    }

    /**
     * Get the current GCLID
     */
    getGclid(): string | null {
        return this.gclid
    }

    /**
     * Set attribution parameters for conversion tracking.
     * These are used for campaign attribution in GA4 funnel reports.
     * Includes UTM params and Google Ads specific params.
     */
    setAttributionParams(params: {
        utm_source?: string
        utm_medium?: string
        utm_campaign?: string
        utm_term?: string
        utm_content?: string
        adset?: string
        network?: string
        matchtype?: string
        device?: string
    }): void {
        const hasValues =
            params.utm_source ||
            params.utm_medium ||
            params.utm_campaign ||
            params.utm_term ||
            params.utm_content ||
            params.adset ||
            params.network ||
            params.matchtype ||
            params.device
        if (!hasValues) return

        // Skip if values haven't changed (reduces log noise from ClickTracker on each page)
        const unchanged =
            params.utm_source === this.utmSource &&
            params.utm_medium === this.utmMedium &&
            params.utm_campaign === this.utmCampaign &&
            params.utm_term === this.utmTerm &&
            params.utm_content === this.utmContent &&
            params.adset === this.adset &&
            params.network === this.network &&
            params.matchtype === this.matchtype &&
            params.device === this.device
        if (unchanged) return

        // Don't overwrite existing real values with defaults ('direct', 'website')
        // This preserves UTM params captured on landing page when user navigates
        this.utmSource = resolveUtmValue(params.utm_source, this.utmSource)
        this.utmMedium = resolveUtmValue(params.utm_medium, this.utmMedium)
        this.utmCampaign = resolveUtmValue(
            params.utm_campaign,
            this.utmCampaign,
        )
        if (params.utm_term) this.utmTerm = params.utm_term
        if (params.utm_content) this.utmContent = params.utm_content
        if (params.adset) this.adset = params.adset
        if (params.network) this.network = params.network
        if (params.matchtype) this.matchtype = params.matchtype
        if (params.device) this.device = params.device
        debugLog('Attribution params set:', params)
    }

    /**
     * @deprecated Use setAttributionParams instead
     */
    setUtmParams(params: {
        utm_source?: string
        utm_medium?: string
        utm_campaign?: string
    }): void {
        this.setAttributionParams(params)
    }

    /**
     * Get session ID from GA4's session cookie for proper session stitching.
     * Cookie name: _ga_<STREAM_ID> (e.g., _ga_QCP7BKQ7WJ for G-QCP7BKQ7WJ)
     * Cookie formats:
     *   GS1: GS1.1.<session_id>.<session_count>.<engaged>.<timestamp>...
     *   GS2: GS2.1.s<session_id>$o<session_count>$g<engaged>$t<timestamp>...
     */
    getSessionId(): string | null {
        if (typeof document === 'undefined') return null

        try {
            // Extract stream ID from measurement ID (remove 'G-' prefix)
            const streamId = this.GA4_MEASUREMENT_ID.replace('G-', '')
            const cookieName = `_ga_${streamId}`

            const sessionCookie = document.cookie
                .split('; ')
                .find((row) => row.startsWith(`${cookieName}=`))
                ?.split('=')[1]

            if (!sessionCookie) return null

            const parts = sessionCookie.split('.')
            if (parts.length >= 3) {
                const sessionPart = parts[2]
                // GS2 format: s{sessionId}$... - extract digits after 's'
                const gs2Match = sessionPart.match(/^s(\d+)/)
                if (gs2Match) {
                    return gs2Match[1]
                }
                // GS1 format: session ID is the value directly
                return sessionPart
            }
        } catch {
            // Ignore cookie parsing errors
        }

        return null
    }

    /**
     * Track a conversion event
     */
    async trackConversion(
        event: GoogleAdsConversionEvent,
        params?: ConversionParams,
    ): Promise<void> {
        // Use provided eventId for cross-provider reconciliation, or generate one
        const eventId = params?.eventId || generateEventId()

        // Queue if not initialized yet - will be processed when initialize() is called
        if (!this.initialized) {
            this.queueConversion(event, params, eventId)
            debugLog('Not initialized, queued conversion:', event)
            return
        }

        // Check cookie consent
        if (!this.hasCookieConsent) {
            this.queueConversion(event, params, eventId)
            return
        }

        await this.sendConversion(event, params, eventId)
    }

    /**
     * Set user data for enhanced conversions
     * Call this after user verification (KYC success) with email/phone
     */
    async setUserData(userData: EnhancedUserData): Promise<void> {
        if (!this.initialized) {
            // Store for later if not initialized
            this.pendingUserData = userData
            return
        }

        if (!this.hasCookieConsent) {
            // Store for later if no consent
            this.pendingUserData = userData
            this.savePendingToStorage()
            return
        }

        await this.sendUserData(userData)
    }

    /**
     * Process pending conversions when consent is granted
     */
    async processPendingConversions(): Promise<void> {
        if (!this.initialized || !this.hasCookieConsent) {
            return
        }

        // Now that consent is granted, persist all in-memory data to storage (GDPR compliance)
        this.persistAllStorageData()

        // Send pending user data first for enhanced matching
        if (this.pendingUserData) {
            await this.sendUserData(this.pendingUserData)
            this.pendingUserData = null
        }

        // Send pending conversions
        const pending = [...this.pendingConversions]
        this.pendingConversions = []
        this.clearPendingFromStorage()

        for (const { event, params, eventId } of pending) {
            await this.sendConversion(event, params, eventId)
        }

        if (pending.length > 0) {
            debugLog(`Processed ${pending.length} pending conversions`)
        }
    }

    /**
     * Persist all in-memory data to storage once consent is granted.
     * Called from processPendingConversions() when consent is obtained.
     */
    private persistAllStorageData(): void {
        if (!this.hasCookieConsent) return

        // Persist client ID to localStorage
        if (notNullish(this.clientId)) {
            try {
                localStorage.setItem(this.CLIENT_ID_KEY, this.clientId)
            } catch {
                // Ignore storage errors
            }
        }

        // Persist GCLID to sessionStorage
        this.saveGclidToStorage()

        // Persist current UTM params to sessionStorage
        this.saveUtmParamsToStorage()

        // Persist original UTM params to localStorage
        this.saveOriginalUtmParams()

        debugLog('Persisted all storage data after consent')
    }

    /**
     * Get or generate a client ID for GA4 user stitching.
     * Attempts to read from GA4's _ga cookie first for proper session stitching,
     * then falls back to localStorage, then generates a new UUID.
     * For GDPR compliance, localStorage writes are deferred until consent.
     */
    private initializeClientId(): void {
        if (typeof window === 'undefined') return

        try {
            // First, try to get client ID from GA4's _ga cookie for proper session stitching
            // Cookie format: GA1.1.<client_id>.<timestamp> or GA1.2.<client_id>.<timestamp>
            const gaClientId = this.getGA4ClientIdFromCookie()
            if (gaClientId) {
                this.clientId = gaClientId
                // Only persist to localStorage if consent granted (GDPR compliance)
                if (this.hasCookieConsent) {
                    localStorage.setItem(this.CLIENT_ID_KEY, gaClientId)
                }
                debugLog('Using GA4 _ga cookie client ID:', gaClientId)
                return
            }

            // Fallback: Try to get existing client ID from localStorage
            const stored = localStorage.getItem(this.CLIENT_ID_KEY)
            if (stored) {
                this.clientId = stored
                return
            }

            // Last resort: Generate new UUID v4 client ID (keep in memory)
            this.clientId = crypto.randomUUID()
            // Only persist to localStorage if consent granted (GDPR compliance)
            if (this.hasCookieConsent) {
                localStorage.setItem(this.CLIENT_ID_KEY, this.clientId)
            }
        } catch {
            // Fallback if localStorage is not available
            this.clientId = crypto.randomUUID()
        }
    }

    /**
     * Extract client ID from GA4's _ga cookie.
     * Cookie format: GA1.1.<client_id>.<timestamp> or GA1.2.<client_id>.<timestamp>
     * The actual client_id for GA4 is "<client_id>.<timestamp>" (last two parts)
     */
    private getGA4ClientIdFromCookie(): string | null {
        if (typeof document === 'undefined') return null

        try {
            const gaCookie = document.cookie
                .split('; ')
                .find((row) => row.startsWith('_ga='))
                ?.split('=')[1]

            if (!gaCookie) return null

            // Format: GA1.1.123456789.1234567890 or GA1.2.123456789.1234567890
            const parts = gaCookie.split('.')
            if (parts.length >= 4) {
                // Client ID is the last two parts: <random_id>.<timestamp>
                return `${parts[2]}.${parts[3]}`
            }
        } catch {
            // Ignore cookie parsing errors
        }

        return null
    }

    /**
     * Send conversion event via server-side GA4 Measurement Protocol
     */
    private async sendConversion(
        event: GoogleAdsConversionEvent,
        params?: ConversionParams,
        eventId?: string,
    ): Promise<void> {
        const config = CONVERSION_LABELS[event]
        if (!config) {
            Sentry.captureMessage(
                'Google Ads conversion label not configured',
                {
                    level: 'warning',
                    tags: {
                        errorType: 'missing_conversion_label',
                        analytics: 'google_ads',
                    },
                    extra: { event },
                },
            )
            return
        }

        // Get the GA4 event name with environment prefix
        const ga4EventName = getGA4EventName(event)

        // Build request body for server-side tracking
        const requestBody: {
            event: string
            clientId: string
            eventId?: string
            sessionId?: string
            userId?: string
            transactionId?: string
            value?: number
            currency?: string
            userData?: HashedUserData
            gclid?: string
            flowType?: string
            isRecoveryFlow?: boolean
            hasCookieConsent?: boolean
            // UTM params for campaign attribution (current/last-touch)
            utmSource?: string
            utmMedium?: string
            utmCampaign?: string
            utmTerm?: string
            utmContent?: string
            // Google Ads specific params
            adset?: string
            network?: string
            matchtype?: string
            device?: string
            // Original params for first-touch attribution
            originalUtmSource?: string
            originalUtmMedium?: string
            originalUtmCampaign?: string
            originalUtmTerm?: string
            originalUtmContent?: string
            originalAdset?: string
            originalNetwork?: string
            originalMatchtype?: string
            originalDevice?: string
        } = {
            event: ga4EventName,
            clientId: this.getClientId(),
            eventId: eventId || generateEventId(),
            hasCookieConsent: this.hasCookieConsent,
        }

        // Add session ID for GA4 session stitching (connects conversion to web session)
        const sessionId = this.getSessionId()
        if (sessionId) {
            requestBody.sessionId = sessionId
        }

        // Add GCLID for direct click attribution (improves conversion matching)
        if (this.gclid) {
            requestBody.gclid = this.gclid
        }

        // Add flow type for analytics segmentation
        if (this.flowType) {
            requestBody.flowType = this.flowType
        }

        // Add recovery flow flag for segmentation (always include to distinguish false from missing)
        requestBody.isRecoveryFlow = this.isRecoveryFlow

        // Add user ID for cross-device session stitching
        if (this.userId) {
            requestBody.userId = this.userId
        }

        // Add attribution params for campaign attribution (current/last-touch)
        // Uses getAttributionParams() to handle race condition with ClickTracker
        const attrParams = this.getAttributionParams()
        if (attrParams.utmSource) requestBody.utmSource = attrParams.utmSource
        if (attrParams.utmMedium) requestBody.utmMedium = attrParams.utmMedium
        if (attrParams.utmCampaign)
            requestBody.utmCampaign = attrParams.utmCampaign
        if (attrParams.utmTerm) requestBody.utmTerm = attrParams.utmTerm
        if (attrParams.utmContent)
            requestBody.utmContent = attrParams.utmContent
        if (attrParams.adset) requestBody.adset = attrParams.adset
        if (attrParams.network) requestBody.network = attrParams.network
        if (attrParams.matchtype) requestBody.matchtype = attrParams.matchtype
        if (attrParams.device) requestBody.device = attrParams.device

        // Log warning when conversion has no attribution data (helps debug unattributed conversions)
        const hasAttribution = attrParams.utmSource || this.gclid
        if (!hasAttribution) {
            Sentry.addBreadcrumb({
                category: 'attribution',
                message: 'Conversion sent without UTM source or GCLID',
                level: 'warning',
                data: {
                    event: ga4EventName,
                    url:
                        typeof window !== 'undefined'
                            ? window.location.href
                            : 'unknown',
                    hasSessionStorage:
                        typeof window !== 'undefined' &&
                        !!sessionStorage.getItem(this.UTM_PARAMS_KEY),
                },
            })
            debugLog(
                'Warning: Conversion without attribution:',
                ga4EventName,
                window?.location?.href,
            )
        }

        // Add original params for first-touch attribution
        // These persist for 30 days and show original acquisition source
        if (this.originalUtmSource)
            requestBody.originalUtmSource = this.originalUtmSource
        if (this.originalUtmMedium)
            requestBody.originalUtmMedium = this.originalUtmMedium
        if (this.originalUtmCampaign)
            requestBody.originalUtmCampaign = this.originalUtmCampaign
        if (this.originalUtmTerm)
            requestBody.originalUtmTerm = this.originalUtmTerm
        if (this.originalUtmContent)
            requestBody.originalUtmContent = this.originalUtmContent
        if (this.originalAdset) requestBody.originalAdset = this.originalAdset
        if (this.originalNetwork)
            requestBody.originalNetwork = this.originalNetwork
        if (this.originalMatchtype)
            requestBody.originalMatchtype = this.originalMatchtype
        if (this.originalDevice)
            requestBody.originalDevice = this.originalDevice

        // Add transaction ID for deduplication
        if (params?.transactionId) {
            requestBody.transactionId = params.transactionId
        }

        // Add value if provided and configured
        if (params?.value !== undefined && config.trackValue) {
            requestBody.value = params.value
            requestBody.currency = params.currency || config.currency || 'USD'
        }

        // Add hashed user data for enhanced conversions if available
        if (this.hashedUserData) {
            requestBody.userData = this.hashedUserData
        }

        try {
            const response = await fetch('/api/track/conversion', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            })

            if (!response.ok) {
                Sentry.captureMessage('Server-side conversion API failed', {
                    level: 'warning',
                    tags: {
                        errorType: 'server_conversion_failed',
                        analytics: 'google_ads',
                    },
                    extra: {
                        event,
                        ga4EventName,
                        status: response.status,
                        transactionId: params?.transactionId,
                    },
                })
            }

            debugLog('Tracked conversion:', ga4EventName, requestBody)
        } catch (error) {
            Sentry.captureException(error, {
                tags: {
                    errorType: 'server_conversion_error',
                    analytics: 'google_ads',
                },
                extra: {
                    event,
                    ga4EventName,
                    transactionId: params?.transactionId,
                },
            })
        }
    }

    /**
     * Prepare and store user data for enhanced conversions.
     * The hashed data will be included in subsequent conversion API calls.
     */
    private async sendUserData(userData: EnhancedUserData): Promise<void> {
        try {
            const hashedData = await prepareUserData(userData)

            // Only store if we have some data
            if (
                hashedData.sha256_email_address ||
                hashedData.sha256_phone_number
            ) {
                this.hashedUserData = hashedData as HashedUserData
                debugLog('Stored enhanced conversion user data for API calls')
            }
        } catch (error) {
            Sentry.captureException(error, {
                tags: {
                    errorType: 'user_data_preparation_failed',
                    analytics: 'google_ads',
                },
                extra: {
                    hasEmail: !!userData.email,
                    hasPhone: !!userData.phone,
                },
            })
        }
    }

    /**
     * Queue conversion for later when consent is granted
     */
    private queueConversion(
        event: GoogleAdsConversionEvent,
        params?: ConversionParams,
        eventId?: string,
    ): void {
        // Prevent memory leaks
        if (this.pendingConversions.length >= this.MAX_PENDING_CONVERSIONS) {
            this.pendingConversions.shift() // Remove oldest
        }

        this.pendingConversions.push({
            event,
            params,
            timestamp: Date.now(),
            eventId: eventId || generateEventId(),
        })

        this.savePendingToStorage()

        debugLog('Queued conversion (no consent):', event)
    }

    /**
     * Save pending data to sessionStorage.
     * Only persists if consent granted (GDPR compliance).
     */
    private savePendingToStorage(): void {
        if (typeof window === 'undefined') return
        // Only persist to sessionStorage if consent granted (GDPR compliance)
        if (!this.hasCookieConsent) return

        try {
            const data = {
                conversions: this.pendingConversions,
                userData: this.pendingUserData,
            }
            sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(data))
        } catch {
            // Ignore storage errors
        }
    }

    /**
     * Load pending data from sessionStorage
     */
    private loadPendingFromStorage(): void {
        if (typeof window === 'undefined') return

        try {
            const stored = sessionStorage.getItem(this.STORAGE_KEY)
            if (stored) {
                const data = JSON.parse(stored)
                this.pendingConversions = data.conversions || []
                this.pendingUserData = data.userData || null
            }
        } catch {
            // Ignore storage errors
        }
    }

    /**
     * Clear pending data from sessionStorage
     */
    private clearPendingFromStorage(): void {
        if (typeof window === 'undefined') return

        try {
            sessionStorage.removeItem(this.STORAGE_KEY)
        } catch {
            // Ignore storage errors
        }
    }

    /**
     * Save GCLID to sessionStorage for persistence across page navigations.
     * Only persists if consent granted (GDPR compliance).
     */
    private saveGclidToStorage(): void {
        if (typeof window === 'undefined' || !this.gclid) return
        // Only persist to sessionStorage if consent granted (GDPR compliance)
        if (!this.hasCookieConsent) return

        try {
            sessionStorage.setItem(this.GCLID_KEY, this.gclid)
        } catch {
            // Ignore storage errors
        }
    }

    /**
     * Load GCLID from sessionStorage
     */
    private loadGclidFromStorage(): void {
        if (typeof window === 'undefined') return

        try {
            const stored = sessionStorage.getItem(this.GCLID_KEY)
            if (stored) {
                this.gclid = stored
                debugLog('Loaded GCLID from storage:', stored)
            }
        } catch {
            // Ignore storage errors
        }
    }

    /**
     * Initialize attribution params from URL or storage.
     *
     * Handles two types of attribution:
     * 1. Current/last-touch (sessionStorage) - params for current session
     * 2. Original/first-touch (localStorage with 30d TTL) - First acquisition source
     *
     * This enables hybrid attribution model:
     * - Recovery users show both their original acquisition source AND re-engagement channel
     * - First-time users have identical current and original values
     */
    private initializeUtmParamsFromUrl(): void {
        if (typeof window === 'undefined') return

        try {
            // Load original params from localStorage (first-touch, persists 30 days)
            this.loadOriginalUtmParams()

            // Load current params from sessionStorage (last-touch, session only)
            const stored = sessionStorage.getItem(this.UTM_PARAMS_KEY)
            if (stored) {
                const params = JSON.parse(stored) as {
                    utmSource?: string
                    utmMedium?: string
                    utmCampaign?: string
                    utmTerm?: string
                    utmContent?: string
                    adset?: string
                    network?: string
                    matchtype?: string
                    device?: string
                }
                if (params.utmSource) this.utmSource = params.utmSource
                if (params.utmMedium) this.utmMedium = params.utmMedium
                if (params.utmCampaign) this.utmCampaign = params.utmCampaign
                if (params.utmTerm) this.utmTerm = params.utmTerm
                if (params.utmContent) this.utmContent = params.utmContent
                if (params.adset) this.adset = params.adset
                if (params.network) this.network = params.network
                if (params.matchtype) this.matchtype = params.matchtype
                if (params.device) this.device = params.device
                debugLog(
                    'Loaded attribution params from session storage:',
                    params,
                )
            }

            // Check current URL for fresh params (overrides session storage)
            const searchParams = new URLSearchParams(window.location.search)
            const utmSource = searchParams.get('utm_source')
            const utmMedium = searchParams.get('utm_medium')
            const utmCampaign = searchParams.get('utm_campaign')
            const utmTerm = searchParams.get('utm_term')
            const utmContent = searchParams.get('utm_content')
            const adset = searchParams.get('adset')
            const network = searchParams.get('network')
            const matchtype = searchParams.get('matchtype')
            const device = searchParams.get('device')

            const hasUrlParams =
                utmSource ||
                utmMedium ||
                utmCampaign ||
                utmTerm ||
                utmContent ||
                adset ||
                network ||
                matchtype ||
                device
            if (hasUrlParams) {
                // Update current/last-touch params
                if (utmSource) this.utmSource = utmSource
                if (utmMedium) this.utmMedium = utmMedium
                if (utmCampaign) this.utmCampaign = utmCampaign
                if (utmTerm) this.utmTerm = utmTerm
                if (utmContent) this.utmContent = utmContent
                if (adset) this.adset = adset
                if (network) this.network = network
                if (matchtype) this.matchtype = matchtype
                if (device) this.device = device
                debugLog('Captured attribution params from URL:', {
                    utmSource,
                    utmMedium,
                    utmCampaign,
                    utmTerm,
                    utmContent,
                    adset,
                    network,
                    matchtype,
                    device,
                })

                // Persist current params to sessionStorage
                this.saveUtmParamsToStorage()

                // If no original params exist, set them (first-touch)
                const hasNoOriginalParams =
                    !this.originalUtmSource &&
                    !this.originalUtmMedium &&
                    !this.originalUtmCampaign &&
                    !this.originalUtmTerm &&
                    !this.originalUtmContent &&
                    !this.originalAdset &&
                    !this.originalNetwork &&
                    !this.originalMatchtype &&
                    !this.originalDevice
                if (hasNoOriginalParams) {
                    this.originalUtmSource = utmSource
                    this.originalUtmMedium = utmMedium
                    this.originalUtmCampaign = utmCampaign
                    this.originalUtmTerm = utmTerm
                    this.originalUtmContent = utmContent
                    this.originalAdset = adset
                    this.originalNetwork = network
                    this.originalMatchtype = matchtype
                    this.originalDevice = device
                    this.saveOriginalUtmParams()
                    debugLog('Set original attribution params (first-touch):', {
                        utmSource,
                        utmMedium,
                        utmCampaign,
                        utmTerm,
                        utmContent,
                        adset,
                        network,
                        matchtype,
                        device,
                    })
                }
            }
        } catch {
            // Ignore storage/parsing errors
        }
    }

    /**
     * Load original attribution params from localStorage (first-touch attribution).
     * These persist for 30 days and are never overwritten by new params.
     */
    private loadOriginalUtmParams(): void {
        if (typeof window === 'undefined') return

        try {
            const stored = localStorageWithTTL.get<{
                utmSource?: string
                utmMedium?: string
                utmCampaign?: string
                utmTerm?: string
                utmContent?: string
                adset?: string
                network?: string
                matchtype?: string
                device?: string
            }>(this.ORIGINAL_UTM_PARAMS_KEY)

            if (stored) {
                if (stored.utmSource) this.originalUtmSource = stored.utmSource
                if (stored.utmMedium) this.originalUtmMedium = stored.utmMedium
                if (stored.utmCampaign)
                    this.originalUtmCampaign = stored.utmCampaign
                if (stored.utmTerm) this.originalUtmTerm = stored.utmTerm
                if (stored.utmContent)
                    this.originalUtmContent = stored.utmContent
                if (stored.adset) this.originalAdset = stored.adset
                if (stored.network) this.originalNetwork = stored.network
                if (stored.matchtype) this.originalMatchtype = stored.matchtype
                if (stored.device) this.originalDevice = stored.device
            }
        } catch {
            // Ignore storage errors
        }
    }

    /**
     * Save original attribution params to localStorage with 30-day TTL.
     * Only called once when user first arrives with attribution params.
     * Only persists if consent granted (GDPR compliance).
     */
    private saveOriginalUtmParams(): void {
        if (typeof window === 'undefined') return
        // Only persist to localStorage if consent granted (GDPR compliance)
        if (!this.hasCookieConsent) return

        const hasParams =
            this.originalUtmSource ||
            this.originalUtmMedium ||
            this.originalUtmCampaign ||
            this.originalUtmTerm ||
            this.originalUtmContent ||
            this.originalAdset ||
            this.originalNetwork ||
            this.originalMatchtype ||
            this.originalDevice
        if (!hasParams) {
            return
        }

        try {
            localStorageWithTTL.set(
                this.ORIGINAL_UTM_PARAMS_KEY,
                {
                    utmSource: this.originalUtmSource,
                    utmMedium: this.originalUtmMedium,
                    utmCampaign: this.originalUtmCampaign,
                    utmTerm: this.originalUtmTerm,
                    utmContent: this.originalUtmContent,
                    adset: this.originalAdset,
                    network: this.originalNetwork,
                    matchtype: this.originalMatchtype,
                    device: this.originalDevice,
                },
                this.ORIGINAL_UTM_TTL_MINUTES,
            )
        } catch {
            // Ignore storage errors
        }
    }

    /**
     * Save attribution params to sessionStorage for persistence across client-side navigation.
     * Always persists to storage - consent check is at send time, not storage time.
     * This ensures attribution isn't lost if user navigates before accepting cookies.
     */
    private saveUtmParamsToStorage(): void {
        if (typeof window === 'undefined') return
        // Always persist attribution to sessionStorage for session recovery.
        // Consent is checked at send time, not storage time.
        // This prevents attribution loss when users navigate before accepting cookies.

        const hasParams =
            this.utmSource ||
            this.utmMedium ||
            this.utmCampaign ||
            this.utmTerm ||
            this.utmContent ||
            this.adset ||
            this.network ||
            this.matchtype ||
            this.device
        if (!hasParams) return

        try {
            const params = {
                utmSource: this.utmSource,
                utmMedium: this.utmMedium,
                utmCampaign: this.utmCampaign,
                utmTerm: this.utmTerm,
                utmContent: this.utmContent,
                adset: this.adset,
                network: this.network,
                matchtype: this.matchtype,
                device: this.device,
            }
            sessionStorage.setItem(this.UTM_PARAMS_KEY, JSON.stringify(params))
        } catch {
            // Ignore storage errors
        }
    }

    /**
     * Get attribution params, falling back to URL if not already set.
     * This handles the race condition where conversion events fire before
     * ClickTracker has a chance to set params via setAttributionParams().
     *
     * Entry points that need this:
     * - External links to /earlypay with UTM params
     * - External links to /earlypay/phone-verification with UTM params
     * - Internal links (localPathnameAction buttons) to /earlypay with UTM params
     * - Internal links to /earlypay/phone-verification with UTM params
     */
    private getAttributionParams(): {
        utmSource: string | null
        utmMedium: string | null
        utmCampaign: string | null
        utmTerm: string | null
        utmContent: string | null
        adset: string | null
        network: string | null
        matchtype: string | null
        device: string | null
    } {
        // If already set via setAttributionParams(), use those values
        const hasValues =
            this.utmSource ||
            this.utmMedium ||
            this.utmCampaign ||
            this.utmTerm ||
            this.utmContent ||
            this.adset ||
            this.network ||
            this.matchtype ||
            this.device
        if (hasValues) {
            return {
                utmSource: this.utmSource,
                utmMedium: this.utmMedium,
                utmCampaign: this.utmCampaign,
                utmTerm: this.utmTerm,
                utmContent: this.utmContent,
                adset: this.adset,
                network: this.network,
                matchtype: this.matchtype,
                device: this.device,
            }
        }

        // Otherwise, try to read from current URL
        if (typeof window === 'undefined') {
            return {
                utmSource: null,
                utmMedium: null,
                utmCampaign: null,
                utmTerm: null,
                utmContent: null,
                adset: null,
                network: null,
                matchtype: null,
                device: null,
            }
        }

        try {
            const searchParams = new URLSearchParams(window.location.search)
            const utmSource = searchParams.get('utm_source')
            const utmMedium = searchParams.get('utm_medium')
            const utmCampaign = searchParams.get('utm_campaign')
            const utmTerm = searchParams.get('utm_term')
            const utmContent = searchParams.get('utm_content')
            const adset = searchParams.get('adset')
            const network = searchParams.get('network')
            const matchtype = searchParams.get('matchtype')
            const device = searchParams.get('device')

            // Cache the values for future calls
            if (utmSource) this.utmSource = utmSource
            if (utmMedium) this.utmMedium = utmMedium
            if (utmCampaign) this.utmCampaign = utmCampaign
            if (utmTerm) this.utmTerm = utmTerm
            if (utmContent) this.utmContent = utmContent
            if (adset) this.adset = adset
            if (network) this.network = network
            if (matchtype) this.matchtype = matchtype
            if (device) this.device = device

            return {
                utmSource,
                utmMedium,
                utmCampaign,
                utmTerm,
                utmContent,
                adset,
                network,
                matchtype,
                device,
            }
        } catch {
            return {
                utmSource: null,
                utmMedium: null,
                utmCampaign: null,
                utmTerm: null,
                utmContent: null,
                adset: null,
                network: null,
                matchtype: null,
                device: null,
            }
        }
    }
}

// Export singleton instance
const googleAdsService = new GoogleAdsService()
export default googleAdsService
